package macha.sh.kotlin.integrations

import androidx.compose.runtime.DisposableEffectResult
import kotlinx.browser.window
import kotlin.js.unsafeCast
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.ref
import org.w3c.dom.HTMLElement

private fun resolvePowerGlitch(): PowerGlitchApi? {
    val candidate = window.asDynamic().PowerGlitch
    return if (candidate != null && candidate != js("undefined")) {
        candidate.unsafeCast<PowerGlitchApi>()
    } else {
        null
    }
}

external interface PowerGlitchApi {
    fun glitch(target: dynamic, options: dynamic = definedExternally): PowerGlitchController
}

external interface PowerGlitchController {
    val play: (() -> Unit)?
    val pause: (() -> Unit)?
    val stop: (() -> Unit)?
    val destroy: (() -> Unit)?
}

enum class PlayMode(internal val value: String) {
    ALWAYS("always"),
    HOVER("hover"),
    CLICK("click"),
    MANUAL("manual")
}

class PowerGlitchOptions internal constructor() {
    internal val data = js("{}")

    private fun ensureObject(key: String): dynamic {
        val existing = data[key]
        if (existing != null && existing != js("undefined") && jsTypeOf(existing) == "object") {
            return existing
        }
        val created = js("{}")
        data[key] = created
        return created
    }

    fun timing(
        durationMs: Int? = null,
        iterations: Double? = null,
        easing: String? = null,
        delayMs: Int? = null
    ) {
        val timing = ensureObject("timing")
        durationMs?.let { timing.duration = it }
        iterations?.let { timing.iterations = it }
        easing?.let { timing.easing = it }
        delayMs?.let { timing.delay = it }
    }

    fun glitchTimeSpan(start: Double? = null, end: Double? = null) {
        val span = ensureObject("glitchTimeSpan")
        start?.let { span.start = it }
        end?.let { span.end = it }
    }

    fun shake(velocity: Int? = null, amplitudeX: Double? = null, amplitudeY: Double? = null) {
        val shake = ensureObject("shake")
        velocity?.let { shake.velocity = it }
        amplitudeX?.let { shake.amplitudeX = it }
        amplitudeY?.let { shake.amplitudeY = it }
    }

    fun slice(
        count: Int? = null,
        velocity: Int? = null,
        minHeight: Double? = null,
        maxHeight: Double? = null,
        hueRotate: Boolean? = null
    ) {
        val slice = ensureObject("slice")
        count?.let { slice.count = it }
        velocity?.let { slice.velocity = it }
        minHeight?.let { slice.minHeight = it }
        maxHeight?.let { slice.maxHeight = it }
        hueRotate?.let { slice.hueRotate = it }
    }

    fun pulse(enabled: Boolean, scale: Double? = null) {
        data.pulse = if (enabled) {
            val pulse = ensureObject("pulse")
            scale?.let { pulse.scale = it }
            pulse
        } else {
            false
        }
    }

    fun html(value: String?) {
        if (value == null) {
            data.html = js("undefined")
        } else {
            data.html = value
        }
    }

    fun playMode(value: PlayMode) {
        data.playMode = value.value
    }

    fun createContainers(value: Boolean) {
        data.createContainers = value
    }

    fun hideOverflow(value: Boolean) {
        data.hideOverflow = value
    }

    internal fun build(): dynamic = data
}

fun powerGlitchOptions(builder: PowerGlitchOptions.() -> Unit): dynamic =
    PowerGlitchOptions().apply(builder).build()

fun applyPowerGlitch(target: HTMLElement, builder: (PowerGlitchOptions.() -> Unit)? = null): PowerGlitchController? {
    val api = resolvePowerGlitch() ?: return null
    return if (builder == null) {
        api.glitch(target)
    } else {
        api.glitch(target, powerGlitchOptions(builder))
    }
}

fun <T : HTMLElement> AttrsScope<T>.powerGlitch(builder: (PowerGlitchOptions.() -> Unit)? = null) {
    ref { element ->
        var controller: PowerGlitchController? = null
        var isInitialized = false

        fun initialize(): Boolean {
            val created = applyPowerGlitch(element, builder) ?: return false
            controller = created
            isInitialized = true
            return true
        }

        val listener: (org.w3c.dom.events.Event) -> Unit = {
            if (!isInitialized) {
                initialize()
            }
        }

        if (!initialize()) {
            window.addEventListener(POWER_GLITCH_READY_EVENT, listener)
        }

        disposeEffect {
            controller?.stop?.invoke()
            controller?.destroy?.invoke()
            window.removeEventListener(POWER_GLITCH_READY_EVENT, listener)
        }
    }
}

// Variant that exposes the created controller to the caller so they can trigger
// glitches programmatically (e.g., in response to an event).
fun <T : HTMLElement> AttrsScope<T>.powerGlitchWithController(
    onReady: (PowerGlitchController) -> Unit,
    builder: (PowerGlitchOptions.() -> Unit)? = null,
) {
    ref { element ->
        var controller: PowerGlitchController? = null
        var isInitialized = false

        fun initialize(): Boolean {
            val created = applyPowerGlitch(element, builder) ?: return false
            controller = created
            isInitialized = true
            onReady(created)
            return true
        }

        val listener: (org.w3c.dom.events.Event) -> Unit = {
            if (!isInitialized) {
                initialize()
            }
        }

        if (!initialize()) {
            window.addEventListener(POWER_GLITCH_READY_EVENT, listener)
        }

        disposeEffect {
            controller?.stop?.invoke()
            controller?.destroy?.invoke()
            window.removeEventListener(POWER_GLITCH_READY_EVENT, listener)
        }
    }
}

const val POWER_GLITCH_READY_EVENT = "powerglitch-ready"

private inline fun disposeEffect(crossinline block: () -> Unit): DisposableEffectResult =
    object : DisposableEffectResult {
        override fun dispose() {
            block()
        }
    }
